// Das Übungsufer (K46): der Kampf läuft live. Die Simulation rechnet ein Stück voraus, damit Ausholen, Würfe und Sprünge
// im Bild rechtzeitig beginnen; ein Befehl gilt ab dem Takt, in dem man ihn gibt, und der Kampf rechnet von dort neu.
// Reine Darstellung: liest den Kampf und gibt Befehle als Eingaben hinein, schreibt nie direkt in den Zustand.
import { combatRules, getAbility, getEnemy, getTrait } from '../../content/index.ts';
import {
  FrameFlag,
  commandTick,
  createLive,
  liveAdvance,
  liveCommand,
  practiceSetup,
  type BattleCommand,
  type BattleEvent,
  type BattleResult,
  type FriendSummary,
  type Live,
  type Order,
  type PracticePlacement,
} from '../../sim/index.ts';
import { figuresWanted, sprite, type ArtSheet, type GlowSpec } from '../art.ts';
import { CLASS_COLORS, ENEMY_COLOR, type DrawUnit } from '../render.ts';
import { leapPosition, poseAt } from '../shore/motion.ts';
import { fxAt, wobbleAt, type ActiveFx } from '../shore/signs.ts';
import { short } from '../shore/state.ts';

/** So weit rechnet die Simulation voraus; reicht für Ausholen (bis 0,4 s) und Sprünge (bis 0,45 s) */
const LOOKAHEAD = 0.75;
/** In diesen Schritten rechnet sie weiter; dazwischen bleibt das Ergebnis dasselbe Objekt (die Bewegungen werden nur dann neu gebaut) */
const CHUNK = 0.25;

export type LiveView = {
  live: Live;
  result: BattleResult;
};

/** Ein neuer Kampf des Übungsufers mit dieser Aufstellung */
export function startLive(placements: PracticePlacement[], seed: number): LiveView {
  const live = createLive(practiceSetup(placements), seed);
  const v: LiveView = { live, result: snapshot(live) };
  return v;
}

/** Was der Kampf bis jetzt weiß, als Ergebnis zum Zeichnen; neues Objekt heißt: Bewegungen neu bauen */
function snapshot(live: Live): BattleResult {
  const b = live.b;
  const friends: FriendSummary[] = b.units
    .filter((u) => u.kind === 'friend')
    .map((u) => ({ uid: u.uid, id: u.runId, name: u.name, species: u.defId, friendClass: u.friendClass!, maxHp: u.maxHp, hpEnd: u.hp, farewell: false, fell: !u.alive && !u.escaped, downBy: null, pushedOut: false, kniffUses: u.kniffUses, treeHpEnd: null, stats: { ...u.stats }, xp: 0 }));
  return { outcome: b.outcome ?? 'loss', duration: b.t, xpPot: 0, escaped: 0, xpLostToEscapes: 0, friends, freed: [], drops: [], units: b.units, events: b.events, frames: b.frames };
}

/** Die Simulation bis kurz hinter die Anzeigezeit bringen */
export function advance(v: LiveView, showT: number): void {
  const b = v.live.b;
  if (b.outcome || b.t >= showT + LOOKAHEAD) return;
  liveAdvance(v.live, showT + LOOKAHEAD + CHUNK);
  v.result = snapshot(v.live);
}

/** Ein Befehl ohne Takt (den setzt command) */
export type Cmd = BattleCommand extends infer C ? (C extends BattleCommand ? Omit<C, 'tick'> : never) : never;

/** K46: ein Befehl ab dem Takt der Anzeigezeit; der Kampf rechnet von dort neu */
export function command(v: LiveView, showT: number, c: Cmd): void {
  liveCommand(v.live, { ...c, tick: commandTick(v.live, showT) } as BattleCommand);
  v.result = snapshot(v.live);
}

export const ended = (v: LiveView): boolean => !!v.live.b.outcome;
export const duration = (v: LiveView): number => v.live.b.t;

/** Der Befehl, den jeder Freund zur Anzeigezeit hat (Befehle aus der Pause gelten ab dem nächsten Takt, darum ein Takt Luft) */
export function ordersAt(r: BattleResult, T: number): Map<number, Order> {
  const out = new Map<number, Order>();
  const limit = T + combatRules.tickSeconds + 1e-6;
  for (const e of r.events) {
    if (e.t > limit) break;
    if (e.type !== 'order') continue;
    if (e.order) out.set(e.unit, e.order);
    else out.delete(e.unit);
  }
  return out;
}

/** Ereignisse in (a, b] */
export function eventsIn(r: BattleResult, a: number, b: number): BattleEvent[] {
  const out: BattleEvent[] = [];
  for (const e of r.events) {
    if (e.t > b + 1e-9) break;
    if (e.t > a + 1e-9) out.push(e);
  }
  return out;
}

/** So viele Sekunden Treffer des Stachelballs auf dasselbe Sturmwesen werden zu einer Zahl [ZAHL?] */
const CALM_WINDOW = 0.6;
const calmCache = new WeakMap<BattleResult, BattleEvent[]>();

/**
 * Ruhigere Zahlen: Der Stachelball trifft alle fünftel Sekunde, das wären Dutzende großer Zahlen übereinander. Darum zählt
 * die Anzeige die Treffer auf dasselbe Sturmwesen über eine gute halbe Sekunde zusammen und zeigt die Summe beim letzten
 * Treffer (nie vorher, damit keine Zahl dem Schlag vorausläuft). Der Treffer, der eine Schwachstelle öffnet, bleibt einzeln.
 */
export function calmEvents(r: BattleResult): BattleEvent[] {
  const done = calmCache.get(r);
  if (done) return done;
  const balls = new Set(r.units.filter((u) => u.kniff && getAbility(u.kniff).mechanic === 'ball').map((u) => u.uid));
  const out: BattleEvent[] = [];
  const open = new Map<string, { at: number; start: number }>();
  for (const e of r.events) {
    if (e.type !== 'damage' || e.source !== 'kniff' || e.from === null || !balls.has(e.from) || e.spotOpen) {
      out.push(e);
      continue;
    }
    const key = `${e.from}:${e.to}`;
    const g = open.get(key);
    const last = g ? out[g.at] : undefined;
    if (g && last?.type === 'damage' && e.t - g.start < CALM_WINDOW - 1e-6) {
      out[g.at] = { ...last, t: e.t, amount: last.amount + e.amount, crit: last.crit || e.crit, weakSpot: last.weakSpot || e.weakSpot };
      continue;
    }
    open.set(key, { at: out.length, start: e.t });
    out.push(e);
  }
  // Die Summe steht beim letzten Treffer: neu ordnen, damit die Zeiten aufsteigen
  out.sort((a, b) => a.t - b.t);
  calmCache.set(r, out);
  return out;
}

/** Bild zur Zeit T (Index) */
export const frameIndex = (r: BattleResult, T: number): number => Math.max(0, Math.min(r.frames.length - 1, Math.floor(T / combatRules.tickSeconds + 1e-6)));

/** Vorschlag des Grafik-Leads für Stärkungen (wie am Ufer) */
const EMPOWERED_GLOW: GlowSpec = { color: '#ffd870', tint: 0.08, alpha: [0.35, 0.75], pulseMs: 1100, width: 1 };

const sheetOf = (kind: string, defId: string): ArtSheet | undefined => (kind === 'stormling' ? sprite('enemy', defId) : sprite('species', defId))?.sheet ?? undefined;

/** Die Figuren zur Anzeigezeit, wie am Ufer (Bewegungen, Bilder der Kniffe, Bögen der Angreifer) */
export function unitsAt(r: BattleResult, T: number): DrawUnit[] {
  if (!r.frames.length) return [];
  const tick = combatRules.tickSeconds;
  const k = Math.max(0, Math.min(r.frames.length - 1, T / tick + 1e-6));
  const i0 = Math.floor(k);
  const i1 = Math.min(r.frames.length - 1, i0 + 1);
  const a = k - i0;
  const f0 = r.frames[i0]!;
  const f1 = r.frames[i1]!;
  const figures = figuresWanted();
  const fx = figures ? fxAt(r, T) : null;
  // K51: Hat ein Kniff die Schwachstelle getroffen, zerspringt die Zielscheibe (schwachstelle-treffer); sie bleibt dann
  // weg, auch solange der Kniff noch doppelt trifft
  const opened = new Set<number>();
  for (const e of r.events) {
    if (e.t > T + 1e-9) break;
    if (e.type === 'damage' && e.spotOpen && sprite('effect', 'schwachstelle-treffer')) opened.add(e.to);
    else if (e.type === 'mark') opened.delete(e.to);
  }
  const list: DrawUnit[] = r.units.map((u) => {
    const unitFx: ActiveFx[] | undefined = opened.has(u.uid) ? fx?.get(u.uid)?.filter((x) => x.id !== 'schwachstelle-ziel') : fx?.get(u.uid);
    const p0 = f0.units[u.uid]!;
    const p1 = f1.units[u.uid]!;
    const jump = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) > 6;
    const leap = figures ? leapPosition(r, u.uid, T) : null;
    const flags = p0[4];
    const glow = unitFx?.find((x) => x.id === 'heisshunger' || x.id === 'hautgift');
    return {
      uid: u.uid,
      side: u.side,
      kind: u.kind,
      name: u.name,
      short: u.kind === 'tree' ? '' : short(u.defId, u.name),
      color: u.kind === 'stormling' ? ENEMY_COLOR : (CLASS_COLORS[u.friendClass ?? ''] ?? '#ccc'),
      radius: u.radius,
      x: leap ? leap.x : jump ? p0[0] : p0[0] + (p1[0] - p0[0]) * a,
      y: leap ? leap.y : jump ? p0[1] : p0[1] + (p1[1] - p0[1]) * a,
      hp: p0[2],
      maxHp: u.maxHp,
      mut: p0[3],
      mutMax: u.mutMax,
      poison: p0[5] ?? 0,
      shield: p0[7] ?? 0,
      // Nach dem Treffer auf die Schwachstelle auch ohne das kleine Zeichen neben der Leiste
      flags: opened.has(u.uid) ? flags & ~FrameFlag.marked : flags,
      flags2: p0[8] ?? 0,
      traits: u.traits.map((t) => getTrait(t).name),
      ...(u.kind === 'tree' ? {} : { art: u.defId, traitIds: u.traits }),
      ...(u.kind === 'stormling' ? { kindName: getEnemy(u.defId).name } : {}),
      ...(u.friendClass ? { friendClass: u.friendClass } : {}),
      ...(figures && u.kind !== 'tree' ? { pose: poseAt(r, u.uid, T, sheetOf(u.kind, u.defId)) } : {}),
      ...(unitFx ? { fx: unitFx } : {}),
      ...(figures && glow ? { glow: sprite(glow.kind, glow.id)?.glow ?? EMPOWERED_GLOW } : figures && flags & FrameFlag.empowered ? { glow: EMPOWERED_GLOW } : {}),
      ...(figures && wobbleAt(r, u.uid, T) ? { wobble: wobbleAt(r, u.uid, T) } : {}),
    };
  });
  // K35, K45: Bögen am Ring, je Sturmwesen, das den Freund gerade angreift
  for (const u of r.units) {
    const d = f0.units[u.uid]!;
    const target = d[6];
    if (u.side !== 'enemy' || (d[4] & FrameFlag.alive) === 0 || target < 0) continue;
    const tu = list[target];
    if (!tu || tu.side !== 'friend' || tu.kind === 'tree') continue;
    tu.attackers = (tu.attackers ?? 0) + 1;
    tu.attackerFrom = [...(tu.attackerFrom ?? []), { x: list[u.uid]!.x, y: list[u.uid]!.y }];
  }
  return list;
}
